import { useQueries, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/api.client'
import { useAuth } from '@/hooks/useAuth'
import type { ApprovalDTO, PageEnvelope } from '@/services/request-center-api'
import { isAdmin, roleAtLeast } from '@/auth'
import { UserRole } from '@kfolks/types'
import type { StatsAdminDTO, StatsMyDTO } from '@kfolks/types'

const STALE_60S = 60_000
const APPROVALS_LIMIT = 10

/**
 * Generic count fetcher used by the cross-module Admin tiles. Accepts
 * both `T[]` and `{ data: T[]; total: number }` response shapes — every
 * existing list endpoint in the codebase returns one of those two.
 */
async function fetchCount(endpoint: string): Promise<number> {
	const res = await apiClient.get<unknown[] | { data: unknown[]; total: number }>(endpoint)
	const body = res.data
	if (body && typeof body === 'object' && !Array.isArray(body) && 'total' in (body as Record<string, unknown>)) {
		return (body as { total: number }).total
	}
	if (Array.isArray(body)) return body.length
	return 0
}

/**
 * Static spec for the 9 admin cross-module tiles. Shape matches the
 * `useQueries({ queries: [...] })` input one-to-one, so the helper hook
 * stays a thin loop. Order is preserved across the destructure in the
 * caller.
 */
const ADMIN_COUNT_QUERY_SPECS: Array<{ key: string; endpoint: string }> = [
	{ key: 'employees', endpoint: '/employees' },
	{ key: 'contractors', endpoint: '/employees/contractors' },
	{ key: 'entra-directory', endpoint: '/entra-directory' },
	{ key: 'clients', endpoint: '/clients' },
	{ key: 'projects', endpoint: '/projects' },
	{ key: 'providers', endpoint: '/providers' },
	{ key: 'idm-requests', endpoint: '/employees/idm-requests' },
	{ key: 'idm-acc-mappings', endpoint: '/employees/idm-acc-mappings' },
	{ key: 'permissions', endpoint: '/permissions' },
]

/**
 * Run the 9 cross-module count tiles in parallel. Each gates via
 * `enabled: admin` so non-admin USERs skip the whole batch.
 */
function useCrossModuleCountQueries(admin: boolean) {
	return useQueries({
		queries: ADMIN_COUNT_QUERY_SPECS.map((spec) => ({
			queryKey: ['dashboard', 'count', spec.key],
			queryFn: () => fetchCount(spec.endpoint),
			enabled: admin,
			staleTime: STALE_60S,
		})),
	})
}

/** Five viewer-side queries (stats/my, open-requests, recent-activity, closed-last-30d, request-types-catalog). */
function useViewerQueries() {
	return useQueries({
		queries: [
			{
				queryKey: ['dashboard', 'stats', 'my'],
				queryFn: async () => (await apiClient.get<StatsMyDTO>('/requests/stats/my')).data,
				staleTime: STALE_60S,
			},
			{
				queryKey: ['dashboard', 'open-requests'],
				queryFn: async () =>
					(
						await apiClient.get<{ data: unknown[]; total: number }>(
							'/requests?filters[submitter]=me&filters[state]=!final&limit=10',
						)
					).data,
				staleTime: STALE_60S,
			},
			{
				queryKey: ['dashboard', 'recent-activity'],
				queryFn: async () => (await apiClient.get<{ data: unknown[] }>('/requests/recent-activity?limit=10')).data,
				staleTime: STALE_60S,
			},
			{
				queryKey: ['dashboard', 'closed-last-30d'],
				queryFn: async () => {
					const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
					const url = `/requests?filters[submitter]=me&filters[state]=completed&filters[from]=${encodeURIComponent(since)}&limit=1`
					return (await apiClient.get<{ data: unknown[]; total: number }>(url)).data
				},
				staleTime: STALE_60S,
			},
			{
				// `MyOpenRequestsWidget` joins `requestTypeId → name` against this
				// catalog to render the human title beside the SR number. Stays
				// fresh enough at 60s to pick up renamed types between sessions.
				queryKey: ['dashboard', 'request-types-catalog'],
				queryFn: async () =>
					(await apiClient.get<Array<{ id: string; name: string }>>('/requests/types?includeInactive=true')).data,
				staleTime: STALE_60S,
			},
		],
	})
}

/** Pending-approvals query gated by the visibility predicate. */
function usePendingApprovalsQuery(visible: boolean) {
	return useQuery({
		queryKey: ['dashboard', 'pending-approvals'],
		queryFn: async () => {
			const res = await apiClient.get<PageEnvelope<ApprovalDTO>>(
				`/requests/approvals?filters[assignedToMe]=true&limit=${APPROVALS_LIMIT}`,
			)
			return res.data
		},
		enabled: visible,
		staleTime: STALE_60S,
	})
}

/**
 * Admin Snapshot (RC) gated on `admin`; IDM-pending-confirmation count (non-RC)
 * gated on `superAdmin` so ADMIN users don't fire /idm-requests fetches that
 * would 403 under ADR-0009 (admin is RC-scoped).
 */
function useAdminCoreQueries(admin: boolean, superAdmin: boolean) {
	const adminStatsQuery = useQuery({
		queryKey: ['dashboard', 'admin-stats'],
		queryFn: async () => (await apiClient.get<StatsAdminDTO>('/requests/stats')).data,
		enabled: admin,
		staleTime: STALE_60S,
	})
	const pendingIdmConfirmationQuery = useQuery({
		queryKey: ['dashboard', 'pending-idm-confirmations'],
		queryFn: async () => fetchCount('/employees/idm-requests?filters[status]=pending_confirmation'),
		enabled: superAdmin,
		staleTime: STALE_60S,
	})
	return { adminStatsQuery, pendingIdmConfirmationQuery }
}

/**
 * Composes the dashboard's data layer for non-VIEWER roles.
 *
 * Per spec (`request-center-dashboard`):
 *   - Dashboard composition is frontend-only — no backend orchestration endpoint.
 *   - TanStack Query caching is applied (60s staleTime + stale-while-revalidate).
 *   - Per-widget failure does not break the page.
 *
 * Two predicates gate widgets internally:
 *   - `pendingApprovalsVisible` — admin OR pendingApprovals > 0 OR hadEverPendingApproval
 *     (drives Pending Approvals + Upcoming SLA visibility).
 *   - `isAdminViewer` — admin shortcut (drives Admin Snapshot, cross-module
 *     counts, Pending Actions callout, Quick Links).
 *
 * Admin queries split by scope (ADR-0009):
 *   - `adminStatsQuery` (RC Admin Snapshot) gates on `admin` — ADMIN + SUPER_ADMIN.
 *   - `pendingIdmConfirmationQuery` and the 9 cross-module counts (all non-RC)
 *     gate on `superAdmin` so ADMIN users never trigger the platform-grade
 *     fetches that would 403 anyway.
 * Non-admin USERs make ZERO admin fetches; ADMIN users make only the RC fetch.
 */
export function useDashboard() {
	const { user } = useAuth()
	const admin = isAdmin(user?.role)
	const superAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)

	const [statsMyQuery, openRequestsQuery, recentActivityQuery, closedLast30dQuery, requestTypesCatalogQuery] =
		useViewerQueries()

	// Visibility predicate: admin OR pendingApprovals > 0 OR hadEverPendingApproval.
	const stats = statsMyQuery.data
	const pendingApprovalsVisible =
		admin || (stats !== undefined && (stats.pendingApprovals > 0 || stats.hadEverPendingApproval))

	const pendingApprovalsQuery = usePendingApprovalsQuery(pendingApprovalsVisible)
	// adminStatsQuery is the RC Admin Snapshot — gated on admin (ADMIN + SUPER_ADMIN).
	// pendingIdmConfirmationQuery feeds the non-RC PendingActionsCallout widget —
	// gated on superAdmin so ADMIN no longer triggers /idm-requests fetches it
	// can't see anyway (ADR-0009).
	const { adminStatsQuery, pendingIdmConfirmationQuery } = useAdminCoreQueries(admin, superAdmin)

	// Cross-module counts feed the non-RC CrossModuleCountsSection widget.
	// Gated on superAdmin so ADMIN doesn't fan out fetches to /employees,
	// /clients, /idm-* etc. that would 403 (ADR-0009).
	const [
		employeesCountQuery,
		contractorsCountQuery,
		entraDirectoryCountQuery,
		clientsCountQuery,
		projectsCountQuery,
		providersCountQuery,
		idmRequestsCountQuery,
		accMappingsCountQuery,
		permissionsCountQuery,
	] = useCrossModuleCountQueries(superAdmin)

	return {
		statsMyQuery,
		openRequestsQuery,
		recentActivityQuery,
		closedLast30dQuery,
		requestTypesCatalogQuery,
		pendingApprovalsQuery,
		/** True iff Pending Approvals + Upcoming SLA widgets should render. */
		pendingApprovalsVisible,

		/** True iff the caller is ADMIN or SUPER_ADMIN. */
		isAdminViewer: admin,

		/** True iff the caller is SUPER_ADMIN (used to gate non-RC affordances). */
		isSuperAdminViewer: superAdmin,

		// Admin overlay queries (only fire when `admin === true`).
		adminStatsQuery,
		pendingIdmConfirmationQuery,
		employeesCountQuery,
		contractorsCountQuery,
		entraDirectoryCountQuery,
		clientsCountQuery,
		projectsCountQuery,
		providersCountQuery,
		idmRequestsCountQuery,
		accMappingsCountQuery,
		permissionsCountQuery,
	}
}
